import {
  CaptionCue,
  CaptionCueWord,
  CaptionsRendererConfig,
} from '@sparemin/text-effects';
import { IManualTranscriptChunk } from 'redux/middleware/api/transcript-service/types';
import { createWordTimestamps } from './transcript/core';

export const DEFAULT_CAPTION_WORD_DURATION_MILLIS = 350;

export function createCaptionCueObject(
  text: string,
  wordDurationMillis: number = DEFAULT_CAPTION_WORD_DURATION_MILLIS,
): CaptionCue {
  const words = text.split(' ');
  const totalDuration = wordDurationMillis * words.length;

  let startMillis = 0;

  const wordsWithTimestamps: CaptionCueWord[] = words.map(word => {
    const endMillis = startMillis + wordDurationMillis;

    const wordObject: CaptionCueWord = {
      text: word,
      startMillis,
      endMillis,
    };

    startMillis = endMillis;

    return wordObject;
  });

  return {
    text,
    startMillis: 0,
    endMillis: totalDuration,
    words: wordsWithTimestamps,
  };
}

export function getDefaultCaptionWordStyling(
  canvasWidth: number,
): Pick<CaptionsRendererConfig, 'wordHighlightStyles' | 'wordUnderlineStyles'> {
  // borderRadius should be 1% of the canvas width.
  const borderRadius = `${canvasWidth / 100}px`;

  return {
    wordHighlightStyles: {
      borderRadius,
    },
    wordUnderlineStyles: {
      borderRadius,
    },
  };
}

/**
 * Converts a manual transcript to a CaptionCue object for the CaptionsRenderer.
 *
 * @param transcript - list of transcript chunks
 * @param offsetMillis - shifts all timestamps in the transcript by this amount
 */
export function convertTranscriptToCues(
  transcript: IManualTranscriptChunk[],
  offsetMillis: number = 0,
): CaptionCue[] {
  if (transcript.length === 0) {
    return [];
  }

  return transcript.flatMap(chunk =>
    chunk.subchunks.map(subchunk => {
      const {
        text: phrase = '',
        words,
        startMillis: phraseStartMillis,
        endMillis: phraseEndMillis,
      } = subchunk;

      const timedWords = Array.isArray(words)
        ? words.flatMap(word =>
            createWordTimestamps(word.text, word.startMillis, word.endMillis),
          )
        : createWordTimestamps(phrase, phraseStartMillis, phraseEndMillis);

      return {
        text: phrase,
        startMillis: phraseStartMillis - offsetMillis,
        endMillis: phraseEndMillis - offsetMillis,
        words: timedWords.map(word => ({
          ...word,
          startMillis: word.startMillis - offsetMillis,
          endMillis: word.endMillis - offsetMillis,
        })),
      };
    }),
  );
}
